import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { inferenceService, CatalogEntry } from '../services/inferenceApi';
import { CATALOG_CATEGORIES, CATALOG_TABS, filterByTab, formatPrice, isNew, toggleCompare } from '../utils/catalog';
import { env } from '../config/env';
import { buildSnippets } from './snippets';
import { copyText } from '../utils/clipboard';
import CategoryTabs from '../components/catalog/CategoryTabs';
import CompareTray from '../components/catalog/CompareTray';
import ModelDetailsModal from '../components/catalog/ModelDetailsModal';
import {
  Page,
  PageHeader,
  LinkButton,
  Tag,
  Tone,
  Skeleton,
  InferenceTabs,
  INFERENCE_EYEBROW,
} from '../components/console/ui';
import { BoltIc, SearchIc } from '../components/console/icons';

const GATEWAY_BASE_URL = `${env.INFERENCE_API_URL.replace(/\/$/, '')}/v1`;

const TRUST_PILLS = [
  'Self-hosted in India',
  'Low latency',
  'Indian data residency',
  'Zero egress',
];

function formatContext(length: number): string {
  if (!length) return '-';
  if (length >= 1000) return `${(length / 1000).toFixed(length % 1000 === 0 ? 0 : 1)}K`;
  return `${length}K`;
}

// Input/output cells. Token-billed rows drop the "/ 1M in|out" suffix (the
// column header carries it); unit-billed rows show their unit in the input
// cell and "-" for output.
function priceCells(entry: CatalogEntry, currency: string): { input: string; output: string } {
  const price = formatPrice(entry, currency);
  if (!price) return { input: '-', output: '-' };
  if (price.output) {
    return {
      input: price.input.replace(' / 1M in', ''),
      output: price.output.replace(' / 1M out', ''),
    };
  }
  return { input: price.input.replace(' / 1M in', ''), output: '-' };
}

const CATEGORY_LABEL = new Map<string, string>(CATALOG_CATEGORIES.map((c) => [c.key, c.label]));

function categoryTone(category: string): Tone {
  if (category === 'text' || category === 'chat') return 'info';
  if (category === 'image' || category === 'video' || category === 'vision') return 'vid';
  if (category === 'embeddings' || category === 'rerank') return 'off';
  return 'accent';
}

const SkeletonRow = () => (
  <tr>
    <td><Skeleton className="h-[18px] w-[18px]" /></td>
    <td>
      <div className="flex items-center gap-2.5">
        <Skeleton className="h-[30px] w-[30px]" />
        <Skeleton className="h-3.5 w-36" />
      </div>
    </td>
    <td><Skeleton className="h-4 w-14 rounded-full" /></td>
    <td><Skeleton className="h-3 w-12" /></td>
    <td><Skeleton className="ml-auto h-3 w-14" /></td>
    <td><Skeleton className="ml-auto h-3 w-14" /></td>
    <td><Skeleton className="ml-auto h-[34px] w-14" /></td>
  </tr>
);

const Models = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [compareSelection, setCompareSelection] = useState<string[]>([]);
  const [detailsEntry, setDetailsEntry] = useState<CatalogEntry | null>(null);

  const rawCategory = searchParams.get('category') ?? 'all';
  const category = CATALOG_TABS.some((t) => t.key === rawCategory) ? rawCategory : 'all';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['catalog'],
    queryFn: inferenceService.getCatalog,
  });

  const models: CatalogEntry[] = data?.models ?? [];
  const currency = data?.currency ?? 'INR';

  const curlSnippet = useMemo(
    () => buildSnippets({ baseUrl: GATEWAY_BASE_URL, model: 'model-name', apiKeyLabel: 'PODSTACK_API_KEY' }).curl,
    []
  );

  const handleCategoryChange = (key: string) => {
    setSearchParams(key === 'all' ? {} : { category: key }, { replace: true });
  };

  const q = search.trim().toLowerCase();
  const searched = q
    ? models.filter(
        (m) =>
          m.display_name.toLowerCase().includes(q) ||
          m.provider.toLowerCase().includes(q) ||
          m.tags.some((t) => t.toLowerCase().includes(q))
      )
    : models;
  const filtered = filterByTab(searched, category);

  const handleCompareToggle = (id: string) => {
    setCompareSelection((prev) => toggleCompare(prev, id));
  };

  return (
    <Page>
      <PageHeader
        eyebrow={INFERENCE_EYEBROW}
        title="Models"
        subtitle="One OpenAI-compatible API for every model. Text is priced per 1M tokens; video and image per 1k frames or images. No pods to manage."
        actions={
          <>
            <LinkButton to="/console/playground" variant="secondary">
              Open playground
            </LinkButton>
            <LinkButton to="/console/keys">Create API key</LinkButton>
          </>
        }
      />

      <InferenceTabs />

      <div className="flex flex-wrap gap-2">
        {TRUST_PILLS.map((pill) => (
          <Tag key={pill} tone="ok">
            {pill}
          </Tag>
        ))}
      </div>

      {isError ? (
        <div className="ps-card px-6 py-14 text-center text-sm text-ps-err">Could not load the model catalog.</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {isLoading ? (
              <div className="flex gap-2">
                <Skeleton className="h-9 w-20 rounded-full" />
                <Skeleton className="h-9 w-24 rounded-full" />
                <Skeleton className="h-9 w-24 rounded-full" />
              </div>
            ) : (
              <CategoryTabs models={models} active={category} onChange={handleCategoryChange} />
            )}
            <span className="flex-1" />
            <label className="flex min-h-[40px] w-full items-center gap-2 rounded-md border border-ps-strong bg-ps-chrome px-3 text-ps-subtle focus-within:border-ps-accent sm:w-[260px]">
              <SearchIc />
              <span className="sr-only">Search models</span>
              <input
                type="search"
                className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-ps-text placeholder-ps-subtle focus:outline-none focus:ring-0"
                placeholder="Search models…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
          </div>

          <div className="ps-card overflow-x-auto">
            <table className="ps-tbl">
              <thead>
                <tr>
                  <th className="w-10"><span className="sr-only">Compare</span></th>
                  <th>Model</th>
                  <th>Type</th>
                  <th>Context</th>
                  <th className="!text-right">Input / 1M</th>
                  <th className="!text-right">Output / 1M</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <>
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                  </>
                ) : filtered.length > 0 ? (
                  filtered.map((m) => {
                    const selected = compareSelection.includes(m.id);
                    const price = priceCells(m, currency);
                    const tone = categoryTone(m.category);
                    return (
                      <tr key={m.id}>
                        <td>
                          <button
                            type="button"
                            onClick={() => handleCompareToggle(m.id)}
                            aria-pressed={selected}
                            aria-label={`Compare ${m.display_name}`}
                            title={selected ? 'Selected for compare' : 'Compare'}
                            className={`grid h-[18px] w-[18px] place-items-center rounded-[4px] border transition-colors ${
                              selected
                                ? 'border-ps-accent bg-ps-accent text-ps-chrome'
                                : 'border-ps-strong bg-ps-chrome hover:border-ps-muted'
                            }`}
                          >
                            {selected && (
                              <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                                <path d="M3 8.5 6.5 12 13 4.5" />
                              </svg>
                            )}
                          </button>
                        </td>
                        <td>
                          <button
                            aria-label={`About ${m.display_name}`}
                            onClick={() => setDetailsEntry(m)}
                            className="group flex min-w-0 items-center gap-2.5 text-left"
                          >
                            <span className="grid h-[30px] w-[30px] flex-none place-items-center rounded-md bg-ps-raised text-xs font-semibold text-ps-text">
                              {m.display_name.charAt(0).toUpperCase()}
                            </span>
                            <span className="min-w-0">
                              <span className="flex items-center gap-2">
                                <span className="font-medium text-ps-text group-hover:text-ps-accent">{m.display_name}</span>
                                {isNew(m.created_at) && <Tag tone="accent">New</Tag>}
                              </span>
                              {m.provider && <span className="block text-xs text-ps-subtle">{m.provider}</span>}
                            </span>
                          </button>
                        </td>
                        <td>
                          {m.category ? (
                            <Tag tone={tone}>
                              {CATEGORY_LABEL.get(m.category) ?? m.category.charAt(0).toUpperCase() + m.category.slice(1)}
                            </Tag>
                          ) : (
                            <span className="text-ps-subtle">-</span>
                          )}
                        </td>
                        <td className="text-ps-muted">{formatContext(m.context_length)}</td>
                        <td className="text-right">{price.input}</td>
                        <td className="text-right">{price.output}</td>
                        <td className="text-right">
                          <Link
                            to={`/console/playground?model=${encodeURIComponent(m.display_name)}`}
                            className="ps-btn2 ps-btn-sm"
                          >
                            Try
                          </Link>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="!whitespace-normal px-5 py-14 text-center">
                      <span className="ps-icb mx-auto mb-3">
                        <BoltIc />
                      </span>
                      <h3 className="text-base font-medium text-ps-text">
                        {q ? `No models match "${search.trim()}"` : 'No models in this category yet'}
                      </h3>
                      <p className="mx-auto mt-1 max-w-sm text-[13px] text-ps-muted">
                        {q
                          ? 'Try a different search term or reset the filter to see all models.'
                          : 'New models are added regularly. Check another category or the docs for the full list.'}
                      </p>
                      {q && (
                        <button onClick={() => setSearch('')} className="ps-btn2 ps-btn-sm mt-4">
                          Clear search
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <section className="ps-card flex flex-col gap-3 p-5" aria-labelledby="call-any-model">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="call-any-model" className="m-0 text-lg font-medium">
            Call any model
          </h2>
          <Link to="/console/docs" className="ps-link text-[13px]">
            Full docs →
          </Link>
        </div>
        <div className="ps-copy">
          <span className="text-ps-subtle">Base URL</span>
          <code>{GATEWAY_BASE_URL}</code>
          <button type="button" className="ps-cb" onClick={() => void copyText(GATEWAY_BASE_URL)}>
            Copy
          </button>
        </div>
        <pre className="ps-pre">{curlSnippet}</pre>
      </section>

      <CompareTray
        models={models}
        selection={compareSelection}
        onRemove={handleCompareToggle}
        onCompare={() =>
          navigate(
            `/console/models/compare?a=${encodeURIComponent(compareSelection[0])}&b=${encodeURIComponent(compareSelection[1])}`
          )
        }
        onClear={() => setCompareSelection([])}
      />

      {detailsEntry && (
        <ModelDetailsModal
          entry={detailsEntry}
          currency={currency}
          onClose={() => setDetailsEntry(null)}
          primaryCta={{
            label: 'Try in playground',
            to: `/console/playground?model=${encodeURIComponent(detailsEntry.display_name)}`,
          }}
          extraActions={
            <button
              className="btn-secondary btn-sm"
              onClick={() => {
                handleCompareToggle(detailsEntry.id);
                setDetailsEntry(null);
              }}
            >
              Add to compare
            </button>
          }
        />
      )}
    </Page>
  );
};

export default Models;
